export type WeatherCondition = "sunny" | "cloudy" | "rain" | "snow" | "wind";

/** Prefer precipitation visuals over wind when both apply. */
export function mapWmoToCondition(
  code: number,
  windSpeed?: number
): WeatherCondition {
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) {
    return "rain";
  }
  if ([71, 73, 75, 77, 85, 86].includes(code)) {
    return "snow";
  }
  // ~22 mph — only when not raining/snowing
  if (windSpeed !== undefined && windSpeed >= 10) {
    return "wind";
  }
  if ([0].includes(code)) return "sunny";
  if ([1, 2, 3, 45, 48].includes(code)) return "cloudy";
  return "cloudy";
}

/**
 * Open-Meteo geocoding often fails on full street addresses.
 * Build fallback queries that drop the street / ZIP toward "City, ST".
 */
export function buildGeocodeQueries(
  ...parts: Array<string | null | undefined>
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  const add = (raw: string) => {
    const q = raw.trim().replace(/\s+/g, " ");
    // Skip empty / state-only scraps like "OH" from comma splits
    if (!q || /^[A-Za-z]{2}$/.test(q) || q.length < 3) return;
    const key = q.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(q);
  };

  for (const part of parts) {
    if (!part?.trim()) continue;
    add(part);

    const segments = part
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    // Drop leading street segment: "502 Cahoon Rd, Bay Village, OH 44140"
    if (segments.length >= 2) {
      add(segments.slice(1).join(", "));
    }
    if (segments.length >= 3) {
      add(segments.slice(-2).join(", "));
    }

    // "Bay Village, OH 44140" → "Bay Village, OH"
    const usCityState = part.match(
      /,\s*([^,]+),\s*([A-Za-z]{2})\s+\d{5}(?:-\d{4})?\s*$/
    );
    if (usCityState) {
      add(`${usCityState[1].trim()}, ${usCityState[2].toUpperCase()}`);
    }

    // Two-segment form: "Bay Village, OH 44140"
    if (segments.length === 2) {
      const cityStateZip = segments[1].match(
        /^([A-Za-z]{2})\s+\d{5}(?:-\d{4})?$/
      );
      if (cityStateZip) {
        add(`${segments[0]}, ${cityStateZip[1].toUpperCase()}`);
      }
    }

    const withoutZip = part
      .replace(/\b\d{5}(?:-\d{4})?\b/g, "")
      .replace(/,\s*,/g, ",")
      .replace(/,\s*$/, "")
      .trim();
    if (withoutZip !== part.trim()) {
      add(withoutZip);
    }
  }

  return out;
}

async function geocode(name: string): Promise<{
  latitude: number;
  longitude: number;
  timezone?: string;
} | null> {
  const geoRes = await fetch(
    `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
      name
    )}&count=1&language=en&format=json`
  );
  const geo = await geoRes.json();
  const place = geo?.results?.[0];
  if (!place) return null;
  return {
    latitude: place.latitude,
    longitude: place.longitude,
    timezone: place.timezone,
  };
}

/**
 * Resolve weather for an event location.
 * Pass address and/or venue/city strings; street addresses are softened for geocoding.
 */
export async function getWeatherCondition(
  locationText: string | string[],
  dateISO: string
): Promise<WeatherCondition> {
  try {
    const inputs = Array.isArray(locationText) ? locationText : [locationText];
    const queries = buildGeocodeQueries(...inputs);

    let place: {
      latitude: number;
      longitude: number;
      timezone?: string;
    } | null = null;
    for (const q of queries) {
      place = await geocode(q);
      if (place) break;
    }
    if (!place) return "cloudy";

    const { latitude, longitude, timezone } = place;
    const date = dateISO.slice(0, 10);
    const wxRes = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&hourly=weathercode,windspeed_10m&timezone=${encodeURIComponent(
        timezone || "auto"
      )}&start_date=${date}&end_date=${date}`
    );
    const wx = await wxRes.json();
    const code = wx?.hourly?.weathercode?.[12] ?? wx?.hourly?.weathercode?.[0];
    const wind =
      wx?.hourly?.windspeed_10m?.[12] ?? wx?.hourly?.windspeed_10m?.[0];
    if (typeof code !== "number") return "cloudy";
    return mapWmoToCondition(code, typeof wind === "number" ? wind : undefined);
  } catch {
    return "cloudy";
  }
}
